/**
 * SD Fitment Widget — vanilla JS, no dependencies.
 *
 * Reads from /pages/fitment-index (JSON) with sessionStorage cache (TTL 30 min).
 * Renders 3 tabs: Application | Vehicle YMM | Bracket (placeholder).
 *
 * Markup contract: each instance is a `.sd-fw[data-fitment-widget]` root containing
 *   - .sd-fw-tabs (tab buttons with [data-tab="application"|"vehicle"|"bracket"])
 *   - .sd-fw-panel[data-panel="..."] (one per tab)
 *   - Inside vehicle panel: .sd-fw-select[data-ymm="year"|"make"|"model"|"trim"], radios [name="sd-fw-side"]
 *   - .sd-fw-btn-primary[data-action="search-vehicle"] for submit
 *   - .sd-fw-results[data-results] containers (split into .sd-fw-results-direct / .sd-fw-results-universal)
 *
 * Asset URL: /assets/fitment-widget.js — referenced by section.
 */

(function () {
  'use strict';

  const CACHE_KEY = 'sd-fitment-index';
  const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
  const FETCH_URL = '/pages/fitment-index';

  // Shared vehicle dataset (assets/unity-fitment.json). Unity's own application
  // guide is far too large to sit in a per-product metafield — the A/GM/V shafts
  // alone blow past Shopify's 65,535 character cap — so the vehicle lists live in
  // one asset and products carry only shaft_code / kit_code / part_number.
  // Resolved off this script's own src so the CDN prefix comes along for free.
  const SELF = document.currentScript ||
    document.querySelector('script[src*="fitment-widget.js"]');
  // Keep this script's own ?v= cache-buster on the JSON URL. Without it the CDN
  // happily serves a stale dataset long after a theme push.
  const FITMENT_DATA_URL = SELF
    ? SELF.src.replace(/fitment-widget\.js/, 'unity-fitment.json')
    : '/assets/unity-fitment.json';

  // ── Cache ────────────────────────────────────────────────
  function readCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj.timestamp || Date.now() - obj.timestamp > CACHE_TTL_MS) return null;
      return obj.data;
    } catch (_) {
      return null;
    }
  }
  function writeCache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data }));
    } catch (_) {
      /* sessionStorage may be full or disabled */
    }
  }

  // ── Shared vehicle dataset ───────────────────────────────
  // Rows are positional to keep the payload small:
  //   [make, model, year_start, year_end, mount_location, lh_kit, rh_kit]
  function expandRows(rows, fitmentType, side) {
    return rows.map(function (r) {
      return {
        make: r[0],
        model: r[1],
        year_start: r[2],
        year_end: r[3],
        fitment_type: fitmentType,
        trim: null,
        side: side || 'either',
        required_kit_sku: r[5] || null,
        notes: '',
        mount_location: r[4] || null,
        rh_kit: r[6] || null
      };
    });
  }

  function normCode(v) {
    return String(v == null ? '' : v).trim().replace(/^#/, '').toUpperCase();
  }

  // Give every product a vehicle_fitment array. An explicit metafield always wins;
  // otherwise we join on the product's shaft / kit / part number.
  function applySharedFitment(index, fit) {
    if (!index || !index.products || !fit) return index;
    const shafts = fit.shafts || {};
    const brackets = fit.brackets || {};
    const parts = fit.parts || {};
    const handles = fit.handles || {};
    // Handle -> code lookups, because the CSV import left the metafields empty.
    const handleShaft = fit.handleShaft || {};
    const handleKit = fit.handleKit || {};

    index.products.forEach(function (p) {
      if (Array.isArray(p.vehicle_fitment) && p.vehicle_fitment.length) return;

      const part = normCode(p.part_number);
      const kit = normCode(p.kit_code) || normCode(handleKit[p.handle]);
      const shaft = normCode(p.shaft_code) || normCode(handleShaft[p.handle]);

      // Vehicles taken straight from the supplier's own "Fits:" list — the most
      // specific source we have, so it outranks anything looked up by code.
      if (handles[p.handle]) {
        p.vehicle_fitment = expandRows(handles[p.handle], 'direct', p.side);
      // A numeric part number is a factory drop-in for specific vehicles.
      } else if (part && parts[part]) {
        p.vehicle_fitment = expandRows(parts[part], 'direct', p.side);
      } else if (kit && brackets[kit]) {
        p.vehicle_fitment = expandRows(brackets[kit], 'direct', p.side);
      } else if (shaft && shafts[shaft]) {
        // Shaft-letter spotlights bolt on with a bracket — universal, not drop-in.
        p.vehicle_fitment = expandRows(shafts[shaft], 'universal', p.side);
      } else {
        p.vehicle_fitment = p.vehicle_fitment || [];
      }
    });
    return index;
  }

  // ── Fetch JSON index ─────────────────────────────────────
  async function loadIndex() {
    const cached = readCache();
    if (cached) return cached;

    const [firstPage, fitData] = await Promise.all([
      fetchIndexPage(1),
      // A missing or broken dataset must not take the whole widget down — the
      // products that carry their own vehicle_fitment metafield still work.
      fetch(FITMENT_DATA_URL).then(function (res) {
        return res.ok ? res.json() : null;
      }).catch(function () { return null; })
    ]);

    // The index paginates at 50 products; pull the rest and stitch them together.
    const pages = Number(firstPage.pages) || 1;
    if (pages > 1) {
      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, function (_, i) { return fetchIndexPage(i + 2); })
      );
      rest.forEach(function (p) {
        if (p && Array.isArray(p.products)) firstPage.products = firstPage.products.concat(p.products);
      });
    }

    const data = applySharedFitment(firstPage, fitData);
    writeCache(data);
    return data;
  }

  async function fetchIndexPage(page) {
    const url = page > 1 ? `${FETCH_URL}?page=${page}` : FETCH_URL;
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Fitment index fetch failed: ${res.status}`);
    return extractJSON(await res.text());
  }

  // The page is HTML-wrapped (Shopify always applies theme.liquid layout to page templates).
  // Our template embeds the JSON in <script id="sd-fitment-index" type="application/json">.
  // Fallback: if response is pure JSON (legacy {% layout 'none' %} working), parse directly.
  function extractJSON(text) {
    const t = (text || '').trim();
    if (t.startsWith('{')) {
      // Pure JSON response
      return JSON.parse(t);
    }
    const m = text.match(/<script[^>]*id=["']sd-fitment-index["'][^>]*>([\s\S]*?)<\/script>/i);
    if (m && m[1]) return JSON.parse(m[1]);
    throw new Error('Fitment index JSON not found in response');
  }

  // Expose for PDP widget reuse
  window.SDFitmentCache = { read: readCache, write: writeCache, load: loadIndex, key: CACHE_KEY };

  // ── Tab switching ────────────────────────────────────────
  function setupTabs(root) {
    const tabs = root.querySelectorAll('.sd-fw-tab');
    const panels = root.querySelectorAll('.sd-fw-panel');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        if (tab.disabled) return;
        const target = tab.dataset.tab;
        tabs.forEach(t => t.setAttribute('aria-selected', t.dataset.tab === target ? 'true' : 'false'));
        panels.forEach(p => p.classList.toggle('is-active', p.dataset.panel === target));
      });
    });
  }

  // ── Application card filter (Tab 1) ─────────────────────
  function setupApplication(root, data) {
    const cards = root.querySelectorAll('.sd-fw-app-card');
    const resultsContainer = root.querySelector('[data-results="application"]');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        const tagsAttr = card.dataset.tags || '';
        const tags = tagsAttr.split(',').map(t => t.trim()).filter(Boolean);
        cards.forEach(c => c.setAttribute('aria-pressed', c === card ? 'true' : 'false'));
        const matches = data.products.filter(p =>
          tags.some(t => (p.tags || []).includes(t))
        );
        renderResultsSimple(resultsContainer, matches, {
          heading: card.querySelector('.sd-fw-app-card-title')?.textContent.trim() || 'Results',
          subheading: matches.length + ' product' + (matches.length === 1 ? '' : 's') + ' match this application.',
          empty: 'No products yet. Try a different application or contact our expert below.'
        });
      });
    });
  }

  // ── Vehicle YMM (Tab 2) ─────────────────────────────────
  function setupVehicle(root, data) {
    const yearSelect = root.querySelector('.sd-fw-select[data-ymm="year"]');
    const makeSelect = root.querySelector('.sd-fw-select[data-ymm="make"]');
    const modelSelect = root.querySelector('.sd-fw-select[data-ymm="model"]');
    const trimSelect = root.querySelector('.sd-fw-select[data-ymm="trim"]');
    const submitBtn = root.querySelector('[data-action="search-vehicle"]');
    const resultsContainer = root.querySelector('[data-results="vehicle"]');

    if (!yearSelect || !makeSelect || !modelSelect) return;

    // Populate years (descending 2026 -> 1990)
    const currentYear = new Date().getFullYear() + 1;
    // Unity's guide reaches back to the 1920s; take the floor from the data
    // rather than a hardcoded year so classic vehicles stay selectable.
    let startYear = 1990;
    data.products.forEach(function (p) {
      (p.vehicle_fitment || []).forEach(function (r) {
        if (r.year_start && r.year_start < startYear) startYear = r.year_start;
      });
    });
    yearSelect.innerHTML = '<option value="">Year</option>' +
      Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i)
        .map(y => `<option value="${y}">${y}</option>`).join('');

    // Helpers — get unique makes/models/trims constrained by previous selections
    function getRecords() {
      const records = [];
      data.products.forEach(p => {
        (p.vehicle_fitment || []).forEach(rec => records.push({ ...rec, _product: p }));
      });
      return records;
    }
    const allRecords = getRecords();

    function recordMatchesYear(rec, year) {
      if (!year) return true;
      const y = Number(year);
      return y >= rec.year_start && y <= rec.year_end;
    }

    function repopulate() {
      const year = yearSelect.value;
      const make = makeSelect.value;
      const model = modelSelect.value;

      // Makes — distinct, alphabetical, scoped by year
      const makes = Array.from(new Set(
        allRecords.filter(r => recordMatchesYear(r, year)).map(r => r.make)
      )).sort();
      const prevMake = make;
      makeSelect.innerHTML = '<option value="">Make</option>' + makes.map(m => `<option value="${m}">${m}</option>`).join('');
      makeSelect.value = makes.includes(prevMake) ? prevMake : '';
      makeSelect.disabled = !year;

      // Models
      const models = Array.from(new Set(
        allRecords
          .filter(r => recordMatchesYear(r, year) && (!makeSelect.value || r.make === makeSelect.value))
          .map(r => r.model)
      )).sort();
      const prevModel = model;
      modelSelect.innerHTML = '<option value="">Model</option>' + models.map(m => `<option value="${m}">${m}</option>`).join('');
      modelSelect.value = models.includes(prevModel) ? prevModel : '';
      modelSelect.disabled = !makeSelect.value;

      // Trims
      const trims = Array.from(new Set(
        allRecords
          .filter(r => recordMatchesYear(r, year) && (!makeSelect.value || r.make === makeSelect.value) && (!modelSelect.value || r.model === modelSelect.value))
          .map(r => r.trim)
          .filter(Boolean)
      )).sort();
      if (trimSelect) {
        trimSelect.innerHTML = '<option value="">Any trim</option>' + trims.map(t => `<option value="${t}">${t}</option>`).join('');
        trimSelect.disabled = !modelSelect.value || trims.length === 0;
      }

      submitBtn.disabled = !(yearSelect.value && makeSelect.value && modelSelect.value);
    }

    [yearSelect, makeSelect, modelSelect].forEach(s => s.addEventListener('change', repopulate));
    repopulate();

    // Submit
    submitBtn.addEventListener('click', () => {
      const year = Number(yearSelect.value);
      const make = makeSelect.value;
      const model = modelSelect.value;
      const trim = trimSelect ? trimSelect.value : '';
      const sideRadio = root.querySelector('input[name="sd-fw-side"]:checked');
      const side = sideRadio ? sideRadio.value : 'either';

      const direct = [];
      const universal = [];
      data.products.forEach(p => {
        const recs = p.vehicle_fitment || [];
        let matchedAsDirect = false;
        let matchedAsUniversal = false;
        recs.forEach(r => {
          if (r.make !== make) return;
          if (r.model !== model) return;
          if (year < r.year_start || year > r.year_end) return;
          if (trim && r.trim && r.trim !== trim) return;
          if (side !== 'either' && r.side && r.side !== side && r.side !== 'either') return;
          if (r.fitment_type === 'direct') matchedAsDirect = true;
          else matchedAsUniversal = true;
        });
        if (matchedAsDirect) direct.push(p);
        if (matchedAsUniversal && !matchedAsDirect) universal.push(p);
      });

      // Update expert CTA URL with YMM context
      updateExpertCtaUrl(root, { year, make, model });

      renderResultsSplit(resultsContainer, { direct, universal, year, make, model });
    });
  }

  // ── Renderers ────────────────────────────────────────────
  function productCardHtml(p) {
    const priceHtml = p.compare_at_price && p.compare_at_price !== '0,00' && p.compare_at_price !== p.price
      ? `<span style="color:#E8A020;font-weight:700;">$${p.price}</span> <span style="text-decoration:line-through;color:#9CA3AF;font-size:.875rem;">$${p.compare_at_price}</span>`
      : `<span style="color:#0d1b2e;font-weight:700;">$${p.price}</span>`;
    const vendor = p.vendor ? `<div style="font-size:.6875rem;font-weight:700;color:#E8A020;text-transform:uppercase;letter-spacing:.06em;margin-bottom:.25rem;">${escape(p.vendor)}</div>` : '';
    return `
      <a href="${p.url}" style="display:block;background:#fff;border:1px solid #e5e2dc;border-radius:12px;overflow:hidden;text-decoration:none;color:inherit;transition:transform .2s,box-shadow .2s,border-color .2s;"
        onmouseover="this.style.transform='translateY(-2px)';this.style.borderColor='#e8a020';this.style.boxShadow='0 8px 24px rgba(0,0,0,.08)';"
        onmouseout="this.style.transform='';this.style.borderColor='#e5e2dc';this.style.boxShadow='';">
        <div style="aspect-ratio:1/1;background:#f8f7f4;display:flex;align-items:center;justify-content:center;overflow:hidden;">
          ${p.image ? `<img src="${p.image}" alt="${escape(p.title)}" loading="lazy" style="width:100%;height:100%;object-fit:cover;">` : ''}
        </div>
        <div style="padding:1rem 1.125rem 1.25rem;">
          ${vendor}
          <div style="font-family:var(--sd-font-heading,inherit);font-size:.9375rem;font-weight:600;line-height:1.4;color:#0d1b2e;margin-bottom:.5rem;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${escape(p.title)}</div>
          <div style="font-size:1rem;">${priceHtml}</div>
        </div>
      </a>
    `;
  }

  function renderResultsSimple(container, products, opts) {
    if (!container) return;
    if (!products.length) {
      container.innerHTML = `<div class="sd-fw-empty"><p>${escape(opts.empty || 'No matches found.')}</p></div>`;
      return;
    }
    container.innerHTML = `
      <div class="sd-fw-results-section">
        <h3 class="sd-fw-results-heading">${escape(opts.heading)}</h3>
        ${opts.subheading ? `<p class="sd-fw-results-sub">${escape(opts.subheading)}</p>` : ''}
        <div class="sd-fw-results-grid">${products.map(productCardHtml).join('')}</div>
      </div>
    `;
  }

  function renderResultsSplit(container, { direct, universal, year, make, model }) {
    if (!container) return;
    const ymmLabel = `${year} ${make} ${model}`;
    if (direct.length === 0 && universal.length === 0) {
      container.innerHTML = `
        <div class="sd-fw-empty">
          <p>No exact match for <strong>${escape(ymmLabel)}</strong>. Talk to our spotlight expert and we'll help you find the right setup.</p>
          <a href="/pages/ask-a-question?year=${encodeURIComponent(year)}&make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}" class="sd-fw-btn-primary" style="text-decoration:none;display:inline-flex;">Ask an Expert →</a>
        </div>
      `;
      return;
    }
    let html = '';
    if (direct.length) {
      html += `
        <div class="sd-fw-results-section">
          <h3 class="sd-fw-results-heading">Direct-fit for your ${escape(ymmLabel)}</h3>
          <p class="sd-fw-results-sub">Drop-in factory replacements — no bracket needed.</p>
          <div class="sd-fw-results-grid">${direct.map(productCardHtml).join('')}</div>
        </div>
      `;
    }
    if (universal.length) {
      html += `
        <div class="sd-fw-results-section">
          <h3 class="sd-fw-results-heading">Also fits with bracket kit</h3>
          <p class="sd-fw-results-sub">Universal spotlights compatible with your vehicle.</p>
          <div class="sd-fw-results-grid">${universal.map(productCardHtml).join('')}</div>
        </div>
      `;
    }
    container.innerHTML = html;
  }

  function updateExpertCtaUrl(root, ctx) {
    const cta = root.querySelector('.sd-fw-expert-cta a');
    if (!cta) return;
    const params = new URLSearchParams();
    if (ctx.year) params.set('year', ctx.year);
    if (ctx.make) params.set('make', ctx.make);
    if (ctx.model) params.set('model', ctx.model);
    cta.href = '/pages/ask-a-question' + (params.toString() ? '?' + params.toString() : '');
  }

  function escape(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // ── Init ────────────────────────────────────────────────

  // ── Searchable dropdown ──────────────────────────────────
  // The make list runs to 100 entries and the model list is longer still, so a
  // native <select> means scrolling forever. We keep the real <select> as the
  // source of truth (all the cascade logic still reads and writes it) and layer
  // a type-to-filter combobox on top of it.
  function enhanceSelect(select) {
    if (select.dataset.comboReady) return;
    select.dataset.comboReady = '1';

    const wrap = document.createElement('div');
    wrap.className = 'sd-fw-combo';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sd-fw-combo-btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    const panel = document.createElement('div');
    panel.className = 'sd-fw-combo-panel';
    panel.hidden = true;
    const search = document.createElement('input');
    search.type = 'text';
    search.className = 'sd-fw-combo-search';
    search.setAttribute('autocomplete', 'off');
    search.setAttribute('aria-label', 'Filter options');
    const list = document.createElement('ul');
    list.className = 'sd-fw-combo-list';
    list.setAttribute('role', 'listbox');
    panel.appendChild(search);
    panel.appendChild(list);

    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);
    wrap.appendChild(btn);
    wrap.appendChild(panel);
    select.classList.add('sd-fw-select--native');

    let active = -1;

    function options() {
      return Array.from(select.options);
    }
    function placeholder() {
      const first = select.options[0];
      return first ? first.textContent : '';
    }
    function syncButton() {
      const opt = select.selectedOptions[0];
      const isPlaceholder = !select.value;
      btn.textContent = opt ? opt.textContent : placeholder();
      btn.classList.toggle('is-placeholder', isPlaceholder);
      btn.disabled = select.disabled;
      if (select.disabled) close();
    }
    function render() {
      const q = search.value.trim().toLowerCase();
      const items = options().filter(function (o) {
        if (!o.value) return false; // the placeholder is never a choice
        return !q || o.textContent.toLowerCase().includes(q);
      });
      list.innerHTML = '';
      if (!items.length) {
        const li = document.createElement('li');
        li.className = 'sd-fw-combo-empty';
        li.textContent = 'No match';
        list.appendChild(li);
        active = -1;
        return;
      }
      items.forEach(function (o, i) {
        const li = document.createElement('li');
        li.className = 'sd-fw-combo-opt';
        li.setAttribute('role', 'option');
        li.textContent = o.textContent;
        li.dataset.value = o.value;
        if (o.value === select.value) li.classList.add('is-selected');
        if (i === active) li.classList.add('is-active');
        li.addEventListener('mousedown', function (ev) {
          ev.preventDefault();
          choose(o.value);
        });
        list.appendChild(li);
      });
      if (active >= items.length) active = items.length - 1;
    }
    function choose(value) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      close();
      syncButton();
    }
    function open() {
      if (select.disabled) return;
      document.querySelectorAll('.sd-fw-combo-panel:not([hidden])').forEach(function (p) {
        if (p !== panel) p.hidden = true;
      });
      panel.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      search.value = '';
      search.placeholder = 'Search ' + placeholder().toLowerCase() + '\u2026';
      active = -1;
      render();
      search.focus();
    }
    function close() {
      panel.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
    }
    function move(delta) {
      const items = list.querySelectorAll('.sd-fw-combo-opt');
      if (!items.length) return;
      active = (active + delta + items.length) % items.length;
      items.forEach(function (el, i) { el.classList.toggle('is-active', i === active); });
      items[active].scrollIntoView({ block: 'nearest' });
    }

    btn.addEventListener('click', function () {
      panel.hidden ? open() : close();
    });
    search.addEventListener('input', function () { active = -1; render(); });
    search.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        const el = list.querySelectorAll('.sd-fw-combo-opt')[active] || list.querySelector('.sd-fw-combo-opt');
        if (el) choose(el.dataset.value);
      } else if (e.key === 'Escape') { e.preventDefault(); close(); btn.focus(); }
    });
    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target)) close();
    });

    // The cascade rewrites select.innerHTML on every change, so mirror it.
    new MutationObserver(function () {
      syncButton();
      if (!panel.hidden) render();
    }).observe(select, { childList: true, attributes: true, attributeFilter: ['disabled'] });

    syncButton();
  }

  function enhanceSelects(root) {
    root.querySelectorAll('.sd-fw-select[data-ymm]').forEach(enhanceSelect);
  }

  async function initWidget(root) {
    setupTabs(root);
    let data;
    try {
      data = await loadIndex();
    } catch (e) {
      const err = root.querySelector('[data-error]');
      if (err) err.textContent = 'Could not load fitment data. Please refresh.';
      console.error(e);
      return;
    }
    if (!data || !Array.isArray(data.products)) return;
    setupApplication(root, data);
    setupVehicle(root, data);
    enhanceSelects(root);
    const loading = root.querySelector('[data-loading]');
    if (loading) loading.style.display = 'none';
  }

  function bootstrap() {
    document.querySelectorAll('.sd-fw[data-fitment-widget]').forEach(initWidget);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Theme Editor / live preview support — reinit when a section is reloaded
  document.addEventListener('shopify:section:load', function (e) {
    const root = e.target;
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('.sd-fw[data-fitment-widget]').forEach(initWidget);
  });
})();
